import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import "@/App.css";
import Header from './components/Header';
import Footer from './components/Footer';
import Diamond from './components/Diamond';
import InteractiveTree from './components/InteractiveTree';
import CTABlock from './components/CTABlock';
import CaseStudyCarousel from './components/CaseStudyCarousel';
import AnimatedQuestionTree from './components/AnimatedQuestionTree';
import ArcadeSection from './components/ArcadeSection';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import BinaryGameSession from './pages/BinaryGameSession';
import TreePage from './pages/TreePage';
import BinaryFinance from './pages/BinaryFinance';
import SalesPage from './pages/SalesPage';
import NathanielQualifier from './components/NathanielQualifier';
import ContentApprovalTree from './components/ContentApprovalTree';
import CTFDProtocol from './pages/CTFDProtocol';
import CTFDAdmin from './pages/CTFDAdmin';
import CTFDQuiz from './pages/CTFDQuiz';
import ProtectedRoute from './components/ProtectedRoute';
import MhansellRoute from './components/MhansellRoute';

// Section wrapper component
const Section = ({ children, id, paddingTop = 72, paddingBottom = 72, className = '' }) => (
  <section 
    id={id}
    className={`section relative ${className}`}
    style={{ 
      paddingTop: `${paddingTop}px`, 
      paddingBottom: `${paddingBottom}px`,
      maxWidth: 'var(--max-width)',
      margin: '0 auto'
    }}
  >
    {children}
  </section>
);

// Ghost number component
const GhostNumber = ({ number }) => (
  <span className="ghost-number">{number}</span>
);

// Offer Card Component
const OfferCard = ({ 
  badge,
  title, 
  price,
  tagline,
  features = [],
  footnote,
  ctaText = 'Book Now',
  href = '#',
  isHighlighted = false,
  showCta = true,
  onClick,
  className = '' 
}) => {
  return (
    <div 
      className={`offer-card ${isHighlighted ? 'offer-card-highlighted' : ''} ${className}`} 
      data-testid={`offer-card-${title?.toLowerCase().replace(/\s/g, '-')}`}
    >
      {badge && <div className="offer-card-badge">{badge}</div>}
      <div className="offer-card-title">{title}</div>
      <div className="offer-card-price">
        <Diamond color="blue" size="md" />
        {price}
      </div>
      {tagline && <p className="offer-card-tagline">{tagline}</p>}
      {features.length > 0 && (
        <ul className="offer-card-features">
          {features.map((feature, index) => (
            <li key={index} className="offer-card-feature">{feature}</li>
          ))}
        </ul>
      )}
      {footnote && <p className="offer-card-footnote">{footnote}</p>}
      {showCta && (
        <a href={href} target="_blank" rel="noopener noreferrer" className="no-underline" onClick={onClick}>
          <button className="offer-card-btn" data-testid={`book-btn-${title?.toLowerCase().replace(/\s/g, '-')}`}>
            <Diamond color="white" size="sm" />
            {ctaText}
          </button>
        </a>
      )}
    </div>
  );
};

function LandingPage() {
  return (
    <div className="App" style={{ background: 'var(--color-bg)' }}>
      <Header />
      
      {/* SECTION 1: HERO */}
      <Section id="hero" paddingTop={100} paddingBottom={100}>
        <GhostNumber number="01" />
        <div className="relative z-10 hero-content">
          <div className="hero-text">
            <h1 
              className="hero-headline"
              data-testid="hero-headline"
            >
              <span className="hero-headline-bold">You make good decisions</span>
              <br />
              <span className="hero-headline-light">until you don't.</span>
            </h1>
            <p className="hero-subheadline">
              Binary Game identifies who you want to be and builds a system so your decisions get you there, even when you're not at your best.
            </p>
            <p className="hero-caption">
              One session. Less than an hour. A system to guide your decisions to who you want to be.
            </p>
            <div className="hero-ctas">
              <CTABlock />
            </div>
          </div>
          <AnimatedQuestionTree />
        </div>
      </Section>
      
      <hr className="minor" style={{ maxWidth: 'var(--max-width)', margin: '0 auto' }} />
      
      {/* SECTION 2: THE MINI TREE */}
      <Section id="mini-tree" paddingTop={80} paddingBottom={80}>
        <GhostNumber number="02" />
        <div className="relative z-10">
          <span className="label mb-4 block">THE MINI TREE</span>
          <p 
            className="section-intro mb-8"
            data-testid="tree-intro"
          >
            <span className="intro-bold">Try it.</span>{' '}
            <span className="intro-light">Five questions. This is Binary Game. You're already in it.</span>
          </p>
          
          {/* Interactive Decision Tree */}
          <div className="mt-8">
            <InteractiveTree />
          </div>
        </div>
      </Section>
      
      <hr className="minor" style={{ maxWidth: 'var(--max-width)', margin: '0 auto' }} />
      
      {/* SECTION 3: WHAT YOU LEAVE WITH */}
      <Section id="deliverable" paddingTop={72} paddingBottom={72}>
        <GhostNumber number="03" />
        <div className="relative z-10">
          <span className="label mb-4 block">WHAT YOU LEAVE WITH</span>
          <h2 
            className="section-headline mb-6"
            data-testid="deliverable-headline"
          >
            <span className="headline-bold">You leave with a system,</span>{' '}
            <span className="headline-light">not a feeling.</span>
          </h2>
          <p className="body-text" style={{ maxWidth: '720px' }}>
            Not insight. Not a new perspective. A written decision tree, built around who you want to be, stored in your private dashboard, yours to run any time the question comes back.
          </p>
          <p className="body-text mt-4" style={{ maxWidth: '720px' }}>
            You bring the question. The session sorts it into yes or no. By the end, the logic that was living in your head is on the page, organized and usable.
          </p>
          <p className="body-text mt-6 body-bold" style={{ color: 'var(--color-near-black)' }}>
            Coaching helps you think. This gives you something to use when thinking is hard.
          </p>
        </div>
      </Section>
      
      <hr className="minor" style={{ maxWidth: 'var(--max-width)', margin: '0 auto' }} />
      
      {/* SECTION 4: ABOUT THE FACILITATOR */}
      <Section id="about" paddingTop={72} paddingBottom={72}>
        <GhostNumber number="04" />
        <div className="relative z-10">
          <span className="label mb-4 block">ABOUT THE FACILITATOR</span>
          <h2 
            className="section-headline mb-6"
            data-testid="about-headline"
          >
            <span className="headline-bold">Built by someone</span>{' '}
            <span className="headline-light">who needed it first.</span>
          </h2>
          <p className="body-text" style={{ maxWidth: '720px' }}>
            <strong>Marco Hansell</strong> spent 15 years building a multimillion-dollar company on instinct alone. Then lost $35 million. Not because of bad luck. Because the decision logic lived only in his head, with nothing to check it when the pressure came.
          </p>
          <p className="body-text mt-4" style={{ maxWidth: '720px' }}>
            Binary Game is what he built after. It runs on his own finances, his business, his life. Every session he facilitates, he has already run on himself.
          </p>
          <p className="body-text mt-4 body-bold" style={{ maxWidth: '720px', color: 'var(--color-near-black)' }}>
            Not teaching theory. Running the same system daily.
          </p>
        </div>
      </Section>
      
      <hr className="minor" style={{ maxWidth: 'var(--max-width)', margin: '0 auto' }} />
      
      {/* SECTION 5: THE OFFER */}
      <Section id="book-session" paddingTop={80} paddingBottom={100}>
        <GhostNumber number="05" />
        <div className="relative z-10">
          <span className="label mb-4 block">THE OFFER</span>
          <h2 
            className="section-headline mb-2"
            data-testid="offer-headline"
          >
            <span className="headline-bold">Pick your tier.</span>
          </h2>
          <p className="body-text mb-8">
            One decision that needs solving now, or a whole area of your life that keeps coming up?
          </p>
          
          <div className="offer-grid">
            <OfferCard
              badge="START HERE"
              title="Binary Solve"
              price="$300"
              tagline="Solve one decision fast."
              features={[
                '30-minute live session',
                '1 compressed decision tree',
                'Stored in your dashboard'
              ]}
              footnote="Clarity, not installation."
              href="https://book.stripe.com/14A5kE81ddOP7wq9UycMM0o"
              onClick={() => {
                window.gtag('event', 'booking_click', {
                  event_category: 'conversion',
                  event_label: 'Binary_Solve_300'
                });
              }}
            />
            <OfferCard
              badge="MOST POPULAR"
              title="Binary Build"
              price="$1,000"
              tagline="Install a repeatable decision system."
              features={[
                'Deep dive on one domain',
                'Complex decision trees',
                'Stored in your dashboard',
                'Update your tree'
              ]}
              footnote="Built for decisions that keep coming back."
              href="https://book.stripe.com/28EaEYdlxh112c66ImcMM0p"
              isHighlighted={true}
              onClick={() => {
                window.gtag('event', 'booking_click', {
                  event_category: 'conversion',
                  event_label: 'Binary_Build_1000'
                });
              }}
            />
            <OfferCard
              badge="FOR FOUNDERS AND EXECUTIVES"
              title="Binary Command"
              price="$10,000+"
              tagline="Decision infrastructure for team leaders."
              features={[
                'Interlocking systems',
                'Team architecture',
                'Organizational integration',
                'Multiple trees and dashboards'
              ]}
              footnote="Private inquiry only."
              ctaText="Inquire"
              href="https://app.usemotion.com/meet/marco-hansell/binaryconsult"
              showCta={true}
            />
          </div>
          
          <div className="mt-8 text-center">
            <a 
              href="https://app.usemotion.com/meet/marco-hansell/binaryconsult"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 no-underline"
              style={{ 
                fontFamily: 'var(--font-mono)',
                fontSize: '11px',
                color: 'var(--color-mid)'
              }}
              data-testid="discovery-link"
            >
              Not sure which tier is right?
              <span style={{ color: 'var(--color-near-black)', fontWeight: 700 }}>
                Free 15-min call first.
              </span>
            </a>
          </div>
        </div>
      </Section>
      
      <hr className="minor" style={{ maxWidth: 'var(--max-width)', margin: '0 auto' }} />
      
      {/* SECTION 6: CASE STUDIES - Full Width Carousel */}
      <CaseStudyCarousel />
      
      <hr className="minor" style={{ maxWidth: 'var(--max-width)', margin: '0 auto' }} />
      
      {/* SECTION 7: CLOSE */}
      <Section id="closing" paddingTop={72} paddingBottom={72}>
        <GhostNumber number="07" />
        <div className="relative z-10">
          <span className="label mb-4 block">CLOSE</span>
          <h2 
            className="section-headline mb-6"
            data-testid="closing-headline"
          >
            <span className="headline-bold">The question you keep coming back to</span>{' '}
            <span className="headline-light">has an answer.</span>
          </h2>
          <p className="body-text" style={{ maxWidth: '720px' }}>
            You just haven't built the system to find it consistently yet. You bring the decision. The game does the rest. You leave with a clear path, written down, built around who you're trying to become.
          </p>
          
          {/* Final CTA */}
          <div className="mt-8">
            <CTABlock />
          </div>
        </div>
      </Section>
      
      <hr className="minor" style={{ maxWidth: 'var(--max-width)', margin: '0 auto' }} />
      
      {/* SECTION 8: THE ARCADE */}
      <ArcadeSection />
      
      <hr className="minor" style={{ maxWidth: 'var(--max-width)', margin: '0 auto' }} />
      
      {/* SECTION 9: BINARY FINANCE PROMO */}
      <Section id="binary-finance-promo" paddingTop={80} paddingBottom={80}>
        <GhostNumber number="09" />
        <div className="relative z-10">
          <div className="binary-finance-promo-card">
            <div className="binary-finance-promo-spine"></div>
            <div className="binary-finance-promo-content">
              <span className="label mb-2 block" style={{ color: 'var(--color-blue)' }}>BINARY GAME PRESENTS</span>
              <h2 
                className="section-headline mb-4"
                data-testid="binary-finance-headline"
              >
                <span className="headline-bold">Binary Finance</span>
              </h2>
              <p className="body-text mb-6" style={{ maxWidth: '560px' }}>
                Make money but don't feel in control of it? Binary Finance builds clear rules for your spending and your revenue so you stop making the same financial decisions over and over again.
              </p>
              <div className="binary-finance-promo-features">
                <div className="binary-finance-promo-feature">
                  <Diamond color="blue" size="sm" />
                  <span>Revenue rules that change with your income zones</span>
                </div>
                <div className="binary-finance-promo-feature">
                  <Diamond color="blue" size="sm" />
                  <span>Expense controls that give you yes or no before you spend</span>
                </div>
                <div className="binary-finance-promo-feature">
                  <Diamond color="blue" size="sm" />
                  <span>A system built once, used over and over</span>
                </div>
              </div>
              <a 
                href="/binary-finance" 
                className="binary-finance-promo-btn"
                data-testid="binary-finance-promo-cta"
              >
                <Diamond color="white" size="sm" />
                Learn More
              </a>
            </div>
          </div>
        </div>
      </Section>
      
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/tree/:treeId" element={<TreePage />} />
        <Route path="/binary-finance" element={<BinaryFinance />} />
        <Route path="/sales" element={<SalesPage />} />
        <Route path="/sales/nathaniel" element={<NathanielQualifier />} />
        <Route path="/sales/aill-content-approval" element={<ContentApprovalTree />} />
        <Route path="/ctfdprotocol" element={<CTFDProtocol />} />
        <Route path="/ctfdprotocol/admin" element={<CTFDAdmin />} />
        <Route path="/ctfdprotocol/quiz" element={<CTFDQuiz />} />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/binary-game" 
          element={
            <MhansellRoute>
              <BinaryGameSession />
            </MhansellRoute>
          } 
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
